"use client";

import { useAuth, useUser } from "@clerk/tanstack-react-start";
import { auth, clerkClient } from "@clerk/tanstack-react-start/server";
import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import {
  ActivityIcon,
  ArrowLeftIcon,
  BuildingIcon,
  ShieldCheckIcon,
  UsersIcon,
} from "lucide-react";
import { Suspense, useEffect } from "react";
import { Header } from "@/components/layout/header";
import { NavGroup } from "@/components/layout/nav-group";
import { NavUser } from "@/components/nav-user";
import { Button } from "@/components/ui/button";
import { FullPageSpinner } from "@/components/ui/full-page-spinner";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

const checkAdminAuth = createServerFn({ method: "GET" }).handler(async () => {
  const { userId } = await auth();

  if (!userId) {
    throw redirect({
      to: "/401",
    });
  }

  const clerk = clerkClient();
  const user = await clerk.users.getUser(userId);
  const isAdmin = user.publicMetadata?.admin === true;

  if (!isAdmin) {
    throw redirect({
      to: "/403",
    });
  }

  return { userId, isAdmin };
});

export const Route = createFileRoute("/admin")({
  beforeLoad: async () => {
    await checkAdminAuth();
  },
  component: AdminLayout,
  ssr: false,
});

const adminNavGroups = [
  {
    title: "Admin",
    items: [
      {
        title: "Dashboard",
        icon: ActivityIcon,
        url: "/admin",
      },
      {
        title: "Organizations",
        icon: BuildingIcon,
        url: "/admin/organizations",
      },
      {
        title: "Users",
        icon: UsersIcon,
        url: "/admin/users",
      },
      {
        title: "Permissions",
        icon: ShieldCheckIcon,
        url: "/admin/permissions",
      },
    ],
  },
];

function AdminLayout() {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();

  // Add client-side check for hydration
  useEffect(() => {
    if (isLoaded && (!isSignedIn || user?.publicMetadata?.admin !== true)) {
      navigate({ to: "/" });
    }
  }, [isLoaded, isSignedIn, user, navigate]);

  if (!isLoaded) {
    return <FullPageSpinner />;
  }

  return (
    <SidebarProvider>
      <Sidebar collapsible="icon" variant="floating">
        <SidebarHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheckIcon className="h-5 w-5" />
            <span className="font-semibold">Admin</span>
          </div>
          <SidebarTrigger />
        </SidebarHeader>
        <SidebarContent>
          {adminNavGroups.map((props) => (
            <NavGroup key={props.title} {...props} />
          ))}
        </SidebarContent>
        <SidebarFooter>
          <Button
            className="w-full justify-start"
            onClick={() => navigate({ to: "/" })}
            variant="ghost"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to App
          </Button>
          <NavUser />
        </SidebarFooter>
      </Sidebar>
      <div
        className={cn(
          "ml-auto w-full max-w-full",
          "peer-data-[state=collapsed]:w-[calc(100%_-_var(--sidebar-width-icon)_-_theme(spacing.4))]",
          "peer-data-[state=expanded]:w-[calc(100%_-_var(--sidebar-width)_-_theme(spacing.4))]",
          "transition-[width] duration-200 ease-linear",
          "flex h-screen flex-col"
        )}
      >
        <Header>
          <div className="flex items-center gap-4">
            <SidebarTrigger />
            <div className="flex items-center gap-2">
              <ShieldCheckIcon className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium text-muted-foreground text-sm">
                Admin Dashboard
              </span>
            </div>
          </div>
        </Header>
        <main className="flex-1 overflow-y-auto">
          <div className="flex-1 space-y-4 p-4 pt-4 md:p-8">
            <Suspense fallback={<FullPageSpinner />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
